import {
  ACCOUNT_STATUSES,
  advertisingAccountCreateSchema,
  APP_STATUSES,
  advertisingPlatformCreateSchema,
  APP_PLATFORMS,
  appCreateSchema,
  BILLING_TYPES,
  currencyCreateSchema,
  expenseCategoryCreateSchema,
  firebaseAccountCreateSchema,
  GST_REGISTRATION_TYPES,
  gstAccountCreateSchema,
  gstRateCreateSchema,
  INDIAN_STATES,
  LABELS,
  managerAccountCreateSchema,
  maskAccountNumber,
  parsePlayStorePackage,
  PLAY_ACCOUNT_TYPES,
  PLAY_VERIFICATION_STATUSES,
  playDeveloperAccountCreateSchema,
  type PlayStoreAppInfo,
  RECORD_STATUSES,
  REVENUE_SOURCE_TYPES,
  revenueAccountCreateSchema,
  revenueSourceCreateSchema,
  stateFromGstin,
} from '@mm/shared';
import { api } from '../../api/client';
import { AppIcon, AppIcons } from '../../components/AppIcon';
import { Badge, StatusBadge } from '../../components/ui/display';
import type { FieldDef, FormValues, Option } from '../../forms/ResourceForm';
import type {
  AdvertisingAccount,
  AdvertisingPlatform,
  App,
  Currency,
  CurrentAssignment,
  ExpenseCategory,
  FirebaseAccount,
  GstAccount,
  GstRate,
  Lookups,
  ManagerAccount,
  RevenueAccount,
  RevenueSource,
  PlayDeveloperAccount,
} from '../../types/models';
import { formatMoney, formatPercent } from '../../utils/format';
import type { FilterDef, MasterConfig } from './MasterPage';
import { sources } from './sources';

// ── Option helpers ───────────────────────────────────────────────────

export const enumOptions = <K extends string>(
  values: readonly K[],
  labels: Record<K, string>,
): Option[] => values.map((v) => ({ value: v, label: labels[v] }));

const statusOptions = enumOptions(RECORD_STATUSES, LABELS.recordStatus);
const accountStatusOptions = enumOptions(ACCOUNT_STATUSES, LABELS.accountStatus);
const appStatusOptions = enumOptions(APP_STATUSES, LABELS.appStatus);
const stateOptions: Option[] = INDIAN_STATES.map((s) => ({ value: s, label: s }));

/** '18.00' and '18' must match in selects. */
export const trimRate = (rate: string | null | undefined) =>
  rate == null ? '' : String(Number(rate));

/** GST rates are a handful of values: searched locally from the cached lookups. */
export const gstRateOptions = (l?: Lookups, c?: FormValues, field = 'defaultGstRate'): Option[] =>
  (l?.gstRates ?? [])
    .filter((r) => r.status === 'ACTIVE' || trimRate(r.rate) === c?.[field])
    .map((r) => ({
      value: trimRate(r.rate),
      label: `${r.label}${r.isDefault ? ' (default)' : ''}`,
    }));

const statusFilter: FilterDef = { name: 'status', label: 'Statuses', options: statusOptions };
const accountStatusFilter: FilterDef = { ...statusFilter, options: accountStatusOptions };
const appStatusFilter: FilterDef = { ...statusFilter, options: appStatusOptions };
/** Accounts currently assigned to the app. */
const appFilter: FilterDef = {
  name: 'appId',
  label: 'Used by app',
  remote: sources.app(sources.all),
};
const platformFilter: FilterDef = {
  name: 'advertisingPlatformId',
  label: 'Platforms',
  remote: sources.platform(sources.all),
};

const statusField: FieldDef = {
  name: 'status',
  label: 'Status',
  type: 'select',
  required: true,
  options: statusOptions,
};
/** Accounts: Active, Inactive, Suspended (apps also have Removed). */
const accountStatusField: FieldDef = {
  ...statusField,
  options: accountStatusOptions,
  help: 'Anything but Active keeps the history but is not used for new entries or assignments',
};
const appStatusField: FieldDef = {
  ...accountStatusField,
  options: appStatusOptions,
  help: 'Removed = taken down from Google Play. Anything but Active keeps the history but is not used for new entries or assignments',
};
const sortOrderField: FieldDef = {
  name: 'sortOrder',
  label: 'Sort order',
  type: 'number',
  help: 'Lower numbers appear first in data entry',
};
const notesField: FieldDef = { name: 'notes', label: 'Notes', type: 'textarea', wide: true };
const loginEmailField: FieldDef = {
  name: 'loginEmail',
  label: 'Login email',
  type: 'text',
  placeholder: 'name@gmail.com',
  help: 'Google / Meta login that owns this account',
};

const accessEmailField: FieldDef = {
  name: 'accessEmail',
  label: 'Access email',
  type: 'text',
  placeholder: 'name@gmail.com',
  help: 'Email that has been given access to this account (e.g. a team member)',
};

const str = (v: unknown) => (v == null ? '' : String(v));

/** "Used by" column: apps with an ACTIVE assignment to the account. */
function UsedBy({ assignments }: { assignments?: CurrentAssignment[] }) {
  const apps = (assignments ?? []).map((a) => a.app);
  if (!apps.length) return <span className="text-slate-400">Not assigned</span>;
  return (
    <span className="flex items-center gap-2">
      <AppIcons apps={apps} size={24} />
      <span className="truncate">{apps.map((a) => a.name).join(', ')}</span>
    </span>
  );
}
const amountOrEmpty = (v: string | null | undefined) =>
  v == null || !Number(v) ? '' : String(Number(v));

// ── Apps ─────────────────────────────────────────────────────────────

export const appsConfig: MasterConfig<App> = {
  resource: 'apps',
  title: 'Apps',
  singular: 'App',
  description: 'Mobile apps whose marketing, revenue and profit you track.',
  searchPlaceholder: 'Search name, package or developer…',
  writePermission: 'masters.write',
  defaultSort: 'name',
  rowLink: (r) => `/apps/${r.id}`,
  filters: [
    appStatusFilter,
    {
      name: 'platform',
      label: 'Platforms',
      options: enumOptions(APP_PLATFORMS, LABELS.appPlatform),
    },
  ],
  columns: [
    {
      key: 'name',
      header: 'App',
      sortKey: 'name',
      render: (r) => (
        <div className="flex items-center gap-3">
          <AppIcon src={r.iconUrl} name={r.name} />
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-900">{r.name}</p>
            {r.packageName && (
              <p className="truncate font-mono text-xs text-slate-500">{r.packageName}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'developerName',
      header: 'Developer account',
      sortKey: 'developerName',
      render: (r) => r.developerName ?? <span className="text-slate-400">—</span>,
    },
    {
      key: 'platform',
      header: 'Platform',
      sortKey: 'platform',
      render: (r) => LABELS.appPlatform[r.platform],
    },
    {
      key: 'accounts',
      header: 'Ad / Revenue accounts',
      align: 'right',
      render: (r) => `${r._count?.adAssignments ?? 0} / ${r._count?.revenueAssignments ?? 0}`,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ],
  schema: appCreateSchema,
  fields: [
    {
      name: 'playStoreUrl',
      label: 'Google Play link',
      type: 'text',
      wide: true,
      placeholder: 'https://play.google.com/store/apps/details?id=com.example.app',
      help: 'Paste the link to fill in name, developer account, logo and package. Support email, dates and About the developer are read from Google Play when you save.',
      lookup: {
        buttonLabel: 'Fetch',
        ready: (v) => parsePlayStorePackage(v) !== null,
        run: async (v, current) => {
          const info = await api.get<PlayStoreAppInfo>('/apps/play-store-lookup', { url: v });
          return {
            values: {
              playStoreUrl: info.playStoreUrl,
              packageName: info.packageName,
              platform: 'ANDROID',
              name: info.name || current.name,
              developerName: info.developerName ?? current.developerName ?? '',
              iconUrl: info.iconUrl ?? current.iconUrl ?? '',
              storeCategory: info.storeCategory ?? current.storeCategory ?? '',
            },
            message: `Found "${info.name}"${info.developerName ? ` by ${info.developerName}` : ''}`,
          };
        },
      },
    },
    { name: 'name', label: 'App name', type: 'text', required: true },
    {
      name: 'developerName',
      label: 'Developer account',
      type: 'text',
      placeholder: 'As shown on Google Play',
    },
    { name: 'packageName', label: 'Package name', type: 'text', placeholder: 'com.example.app' },
    {
      name: 'platform',
      label: 'Platform',
      type: 'select',
      required: true,
      options: enumOptions(APP_PLATFORMS, LABELS.appPlatform),
    },
    { name: 'iconUrl', label: 'Logo URL', type: 'image', wide: true, placeholder: 'https://…' },
    { name: 'storeCategory', label: 'Store category', type: 'text' },
    {
      name: 'developerAccessEmail',
      label: 'Developer account access email',
      type: 'text',
      placeholder: 'name@gmail.com',
      help: 'Email that has been given access to the Google Play developer account',
    },
    appStatusField,
    { name: 'description', label: 'Description', type: 'textarea', wide: true },
    notesField,
  ],
  afterSave: async (saved, previous) => {
    if (saved.playStoreUrl && saved.playStoreUrl !== previous?.playStoreUrl) {
      await api.post(`/apps/${saved.id}/play-store-sync`);
    }
  },
  defaults: () => ({
    playStoreUrl: '',
    name: '',
    developerName: '',
    packageName: '',
    platform: 'ANDROID',
    iconUrl: '',
    storeCategory: '',
    developerAccessEmail: '',
    status: 'ACTIVE',
    description: '',
    notes: '',
  }),
  toForm: (r) => ({
    playStoreUrl: str(r.playStoreUrl),
    name: r.name,
    developerName: str(r.developerName),
    packageName: str(r.packageName),
    platform: r.platform,
    iconUrl: str(r.iconUrl),
    storeCategory: str(r.storeCategory),
    developerAccessEmail: str(r.developerAccessEmail),
    status: r.status,
    description: str(r.description),
    notes: str(r.notes),
  }),
};

// ── Advertising platforms ────────────────────────────────────────────

export const advertisingPlatformsConfig: MasterConfig<AdvertisingPlatform> = {
  resource: 'advertising-platforms',
  title: 'Advertising platforms',
  singular: 'Platform',
  description: 'Where marketing spend happens (Google Ads, Meta Ads, …).',
  writePermission: 'masters.write',
  filters: [statusFilter],
  columns: [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
    },
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (r) => <code className="text-xs">{r.code}</code>,
    },
    { key: 'accounts', header: 'Accounts', align: 'right', render: (r) => r._count?.accounts ?? 0 },
    { key: 'sortOrder', header: 'Order', align: 'right', sortKey: 'sortOrder' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ],
  schema: advertisingPlatformCreateSchema,
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      readOnlyOnEdit: true,
      help: 'e.g. TIKTOK_ADS',
    },
    sortOrderField,
    statusField,
  ],
  defaults: () => ({ name: '', code: '', sortOrder: '0', status: 'ACTIVE' }),
  toForm: (r) => ({ name: r.name, code: r.code, sortOrder: String(r.sortOrder), status: r.status }),
};

// ── Advertising accounts ─────────────────────────────────────────────

const isPrepaid = (v: FormValues) => v.billingType !== 'POSTPAID';

export const advertisingAccountsConfig: MasterConfig<AdvertisingAccount> = {
  resource: 'advertising-accounts',
  title: 'Ads accounts',
  singular: 'Ads account',
  description:
    'Google Ads, Meta Ads and other advertising accounts. Assign them to apps from the app page; one account can serve several apps. Postpaid accounts use their MCC’s credit line.',
  searchPlaceholder: 'Search account, ID, email or MCC…',
  writePermission: 'masters.write',
  rowLink: (r) => `/ads-accounts/${r.id}`,
  filters: [
    appFilter,
    platformFilter,
    { name: 'managerAccountId', label: 'MCCs', remote: sources.managerAccount(sources.all) },
    {
      name: 'billingType',
      label: 'Billing types',
      options: enumOptions(BILLING_TYPES, LABELS.billingType),
    },
    accountStatusFilter,
  ],
  columns: [
    {
      key: 'accountName',
      header: 'Account',
      sortKey: 'accountName',
      render: (r) => (
        <div>
          <p className="font-medium text-slate-900">{r.accountName}</p>
          <p className="text-xs text-slate-500">
            {[r.accountIdentifier, r.loginEmail].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'apps',
      header: 'Used by',
      render: (r) => <UsedBy assignments={r.assignments} />,
    },
    { key: 'platform', header: 'Platform', sortKey: 'platform', render: (r) => r.platform?.name },
    {
      key: 'billing',
      header: 'Billing',
      sortKey: 'billingType',
      render: (r) => (
        <div>
          <Badge tone={r.billingType === 'POSTPAID' ? 'violet' : 'blue'}>
            {r.billingType === 'POSTPAID' ? 'Postpaid' : 'Prepaid'}
          </Badge>
          {r.managerAccount && (
            <p className="mt-0.5 text-xs text-slate-500">{r.managerAccount.name}</p>
          )}
        </div>
      ),
    },
    {
      key: 'gst',
      header: 'GST account',
      render: (r) =>
        r.gstAccount ? (
          <div>
            <p>{r.gstAccount.tradeName || r.gstAccount.legalName}</p>
            <p className="font-mono text-xs text-slate-500">{r.gstAccount.gstin}</p>
          </div>
        ) : (
          <span className="text-slate-400">None</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ],
  schema: advertisingAccountCreateSchema,
  fields: [
    {
      name: 'advertisingPlatformId',
      label: 'Platform',
      type: 'select',
      required: true,
      remote: sources.platform(),
    },
    {
      name: 'accountName',
      label: 'Account name',
      type: 'text',
      required: true,
      placeholder: 'Random Video Call Google Ads',
    },
    { name: 'accountIdentifier', label: 'Account ID', type: 'text', placeholder: '123-456-7890' },
    loginEmailField,
    accessEmailField,
    {
      name: 'billingType',
      label: 'Billing type',
      type: 'select',
      required: true,
      options: enumOptions(BILLING_TYPES, LABELS.billingType),
    },
    {
      name: 'managerAccountId',
      label: 'Manager account (MCC)',
      type: 'select',
      emptyLabel: 'Not linked to an MCC',
      help: 'Required for postpaid; the account is billed to the MCC’s GST account',
      remote: (v) =>
        sources.managerAccount(
          v.advertisingPlatformId
            ? { status: 'ACTIVE', advertisingPlatformId: String(v.advertisingPlatformId) }
            : { status: 'ACTIVE' },
        ),
    },
    {
      name: 'gstAccountId',
      label: 'GST account',
      type: 'select',
      remote: sources.gstAccount(),
      showIf: (v) => !v.managerAccountId,
    },
    {
      name: 'currencyCode',
      label: 'Currency',
      type: 'select',
      required: true,
      remote: sources.currency(),
    },
    {
      name: 'openingBalance',
      label: 'Prepaid opening balance (₹)',
      type: 'decimal',
      showIf: isPrepaid,
      help: 'Balance on the date below, when you started tracking',
    },
    { name: 'openingBalanceDate', label: 'Opening balance date', type: 'date', showIf: isPrepaid },
    {
      name: 'balanceBasis',
      label: 'Balance is used by',
      type: 'select',
      required: true,
      showIf: isPrepaid,
      options: [
        { value: 'INCLUDING_GST', label: 'Spend including GST' },
        { value: 'EXCLUDING_GST', label: 'Spend excluding GST' },
      ],
    },
    sortOrderField,
    accountStatusField,
    notesField,
  ],
  defaults: () => ({
    advertisingPlatformId: '',
    accountName: '',
    accountIdentifier: '',
    loginEmail: '',
    accessEmail: '',
    billingType: 'PREPAID',
    managerAccountId: '',
    gstAccountId: '',
    currencyCode: 'INR',
    openingBalance: '',
    openingBalanceDate: '',
    balanceBasis: 'INCLUDING_GST',
    sortOrder: '0',
    status: 'ACTIVE',
    notes: '',
  }),
  toForm: (r) => ({
    advertisingPlatformId: r.advertisingPlatformId,
    accountName: r.accountName,
    accountIdentifier: str(r.accountIdentifier),
    loginEmail: str(r.loginEmail),
    accessEmail: str(r.accessEmail),
    billingType: r.billingType,
    managerAccountId: str(r.managerAccountId),
    gstAccountId: str(r.gstAccountId),
    currencyCode: r.currencyCode,
    openingBalance: amountOrEmpty(r.openingBalance),
    openingBalanceDate: str(r.openingBalanceDate),
    balanceBasis: r.balanceBasis,
    sortOrder: String(r.sortOrder),
    status: r.status,
    notes: str(r.notes),
  }),
};

// ── Manager accounts (MCC) ───────────────────────────────────────────

export const managerAccountsConfig: MasterConfig<ManagerAccount> = {
  resource: 'manager-accounts',
  title: 'MCC accounts',
  singular: 'Manager account',
  description:
    'Manager accounts (MCC) hold the credit line used by their postpaid advertising accounts.',
  searchPlaceholder: 'Search MCC, ID, email or GSTIN…',
  writePermission: 'masters.write',
  rowLink: (r) => `/mcc-accounts/${r.id}`,
  filters: [platformFilter, statusFilter],
  columns: [
    {
      key: 'name',
      header: 'MCC',
      sortKey: 'name',
      render: (r) => (
        <div>
          <p className="font-medium text-slate-900">{r.name}</p>
          <p className="text-xs text-slate-500">
            {[r.accountIdentifier, r.loginEmail].filter(Boolean).join(' · ')}
          </p>
        </div>
      ),
    },
    { key: 'platform', header: 'Platform', render: (r) => r.platform?.name },
    {
      key: 'gst',
      header: 'GST account',
      render: (r) =>
        r.gstAccount
          ? `${r.gstAccount.tradeName || r.gstAccount.legalName} · ${r.gstAccount.gstin}`
          : '—',
    },
    {
      key: 'creditLimit',
      header: 'Credit limit',
      align: 'right',
      sortKey: 'creditLimit',
      render: (r) => formatMoney(r.creditLimit),
    },
    {
      key: 'accounts',
      header: 'Accounts',
      align: 'right',
      render: (r) => r._count?.advertisingAccounts ?? 0,
    },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ],
  schema: managerAccountCreateSchema,
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Company MCC' },
    {
      name: 'accountIdentifier',
      label: 'MCC customer ID',
      type: 'text',
      placeholder: '123-456-7890',
    },
    { ...loginEmailField, help: 'Google login that owns this MCC' },
    {
      name: 'advertisingPlatformId',
      label: 'Platform',
      type: 'select',
      required: true,
      remote: sources.platform(),
    },
    {
      name: 'gstAccountId',
      label: 'GST account',
      type: 'select',
      required: true,
      remote: sources.gstAccount(),
      help: 'One MCC per GST account per platform',
    },
    {
      name: 'currencyCode',
      label: 'Currency',
      type: 'select',
      required: true,
      remote: sources.currency(),
    },
    {
      name: 'creditLimit',
      label: 'Credit limit (₹)',
      type: 'decimal',
      help: 'Leave empty if this MCC has no credit line',
    },
    {
      name: 'balanceBasis',
      label: 'Credit is used by',
      type: 'select',
      required: true,
      options: [
        { value: 'INCLUDING_GST', label: 'Spend including GST (invoice amount)' },
        { value: 'EXCLUDING_GST', label: 'Spend excluding GST' },
      ],
    },
    {
      name: 'openingUsedAmount',
      label: 'Already used when tracking started (₹)',
      type: 'decimal',
      help: 'Unpaid credit on the date below',
    },
    { name: 'openingBalanceDate', label: 'As of date', type: 'date' },
    statusField,
    notesField,
  ],
  defaults: () => ({
    name: '',
    accountIdentifier: '',
    loginEmail: '',
    advertisingPlatformId: '',
    gstAccountId: '',
    currencyCode: 'INR',
    creditLimit: '',
    balanceBasis: 'INCLUDING_GST',
    openingUsedAmount: '',
    openingBalanceDate: '',
    status: 'ACTIVE',
    notes: '',
  }),
  toForm: (r) => ({
    name: r.name,
    accountIdentifier: str(r.accountIdentifier),
    loginEmail: str(r.loginEmail),
    advertisingPlatformId: r.advertisingPlatformId,
    gstAccountId: r.gstAccountId,
    currencyCode: r.currencyCode,
    creditLimit: r.creditLimit == null ? '' : String(Number(r.creditLimit)),
    balanceBasis: r.balanceBasis,
    openingUsedAmount: amountOrEmpty(r.openingUsedAmount),
    openingBalanceDate: str(r.openingBalanceDate),
    status: r.status,
    notes: str(r.notes),
  }),
};

// ── GST accounts ─────────────────────────────────────────────────────

export const gstAccountsConfig: MasterConfig<GstAccount> = {
  resource: 'gst-accounts',
  title: 'GST accounts',
  singular: 'GST account',
  description:
    'Your GST registrations. Each can have one MCC per platform; expenses are linked for ITC tracking.',
  searchPlaceholder: 'Search GSTIN, name or state…',
  writePermission: 'gstAccounts.write',
  defaultSort: 'legalName',
  filters: [statusFilter, { name: 'state', label: 'States', options: stateOptions }],
  columns: [
    {
      key: 'legalName',
      header: 'Business',
      sortKey: 'legalName',
      render: (r) => (
        <div>
          <p className="font-medium text-slate-900">{r.legalName}</p>
          {r.tradeName && <p className="text-xs text-slate-500">{r.tradeName}</p>}
        </div>
      ),
    },
    {
      key: 'gstin',
      header: 'GSTIN',
      sortKey: 'gstin',
      render: (r) => <span className="font-mono text-xs">{r.gstin}</span>,
    },
    {
      key: 'state',
      header: 'State',
      sortKey: 'state',
      render: (r) => `${r.state} (${r.stateCode})`,
    },
    {
      key: 'registrationType',
      header: 'Registration',
      render: (r) => LABELS.gstRegistrationType[r.registrationType],
    },
    {
      key: 'defaultGstRate',
      header: 'Default rate',
      align: 'right',
      render: (r) => formatPercent(r.defaultGstRate),
    },
    {
      key: 'accounts',
      header: 'Ad accounts',
      align: 'right',
      render: (r) => r._count?.advertisingAccounts ?? 0,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ],
  schema: gstAccountCreateSchema,
  fields: [
    {
      name: 'gstin',
      label: 'GSTIN',
      type: 'text',
      required: true,
      placeholder: '29ABCDE1234F1Z5',
      help: 'Validated including the checksum digit',
    },
    { name: 'legalName', label: 'Legal name', type: 'text', required: true },
    { name: 'tradeName', label: 'Trade name', type: 'text' },
    {
      name: 'state',
      label: 'State',
      type: 'select',
      options: stateOptions,
      emptyLabel: 'Selected from the GSTIN',
      autofill: { from: 'gstin', compute: (v) => (v.length >= 2 ? stateFromGstin(v).name : null) },
      help: 'Chosen automatically from the GSTIN state code',
    },
    {
      name: 'registrationType',
      label: 'Registration type',
      type: 'select',
      required: true,
      options: enumOptions(GST_REGISTRATION_TYPES, LABELS.gstRegistrationType),
    },
    {
      name: 'defaultGstRate',
      label: 'Default GST rate',
      type: 'select',
      required: true,
      options: (l, c) => gstRateOptions(l, c),
    },
    statusField,
    notesField,
  ],
  defaults: (l) => ({
    gstin: '',
    legalName: '',
    tradeName: '',
    state: '',
    registrationType: 'REGULAR',
    defaultGstRate: trimRate(l?.gstRates.find((r) => r.isDefault)?.rate ?? '18'),
    status: 'ACTIVE',
    notes: '',
  }),
  toForm: (r) => ({
    gstin: r.gstin,
    legalName: r.legalName,
    tradeName: str(r.tradeName),
    state: r.state,
    registrationType: r.registrationType,
    defaultGstRate: trimRate(r.defaultGstRate),
    status: r.status,
    notes: str(r.notes),
  }),
};

// ── GST rates ────────────────────────────────────────────────────────

export const gstRatesConfig: MasterConfig<GstRate> = {
  resource: 'gst-rates',
  title: 'GST rates',
  singular: 'GST rate',
  description:
    'Rates offered when entering expenses. Each transaction stores the rate it used, so editing rates never changes history.',
  writePermission: 'masters.write',
  defaultSort: 'rate',
  pageSize: 50,
  columns: [
    {
      key: 'rate',
      header: 'Rate',
      sortKey: 'rate',
      align: 'right',
      render: (r) => formatPercent(r.rate),
    },
    { key: 'label', header: 'Label', sortKey: 'label' },
    {
      key: 'isDefault',
      header: 'Default',
      render: (r) => (r.isDefault ? <Badge tone="violet">Default</Badge> : null),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ],
  schema: gstRateCreateSchema,
  fields: [
    { name: 'rate', label: 'Rate (%)', type: 'decimal', required: true },
    { name: 'label', label: 'Label', type: 'text', required: true, placeholder: '18%' },
    statusField,
    { name: 'isDefault', label: 'Default', type: 'checkbox', help: 'Use as the default rate' },
  ],
  defaults: () => ({ rate: '', label: '', status: 'ACTIVE', isDefault: false }),
  toForm: (r) => ({
    rate: trimRate(r.rate),
    label: r.label,
    status: r.status,
    isDefault: r.isDefault,
  }),
};

// ── Currencies ───────────────────────────────────────────────────────

export const currenciesConfig: MasterConfig<Currency> = {
  resource: 'currencies',
  title: 'Currencies',
  singular: 'Currency',
  description: 'INR is the base currency. Others need a monthly rate under Currency Rates.',
  writePermission: 'masters.write',
  keyOf: (r) => r.code,
  defaultSort: 'code',
  columns: [
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (r) => <span className="font-mono font-medium">{r.code}</span>,
    },
    { key: 'name', header: 'Name', sortKey: 'name' },
    { key: 'symbol', header: 'Symbol' },
    { key: 'decimalPlaces', header: 'Decimals', align: 'right' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ],
  schema: currencyCreateSchema,
  fields: [
    {
      name: 'code',
      label: 'ISO code',
      type: 'text',
      required: true,
      readOnlyOnEdit: true,
      placeholder: 'GBP',
    },
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'symbol', label: 'Symbol', type: 'text', required: true },
    { name: 'decimalPlaces', label: 'Decimal places', type: 'number', required: true },
    statusField,
  ],
  defaults: () => ({ code: '', name: '', symbol: '', decimalPlaces: '2', status: 'ACTIVE' }),
  toForm: (r) => ({
    code: r.code,
    name: r.name,
    symbol: r.symbol,
    decimalPlaces: String(r.decimalPlaces),
    status: r.status,
  }),
};

// ── Revenue sources ──────────────────────────────────────────────────

export const revenueSourcesConfig: MasterConfig<RevenueSource> = {
  resource: 'revenue-sources',
  title: 'Revenue sources',
  singular: 'Revenue source',
  description:
    'AdMob, AdX / Google Ad Manager, Google Play and any future networks. Adding one needs no code change.',
  writePermission: 'masters.write',
  filters: [
    statusFilter,
    {
      name: 'type',
      label: 'Types',
      options: enumOptions(REVENUE_SOURCE_TYPES, LABELS.revenueSourceType),
    },
  ],
  columns: [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
    },
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (r) => <code className="text-xs">{r.code}</code>,
    },
    {
      key: 'type',
      header: 'Type',
      sortKey: 'type',
      render: (r) => LABELS.revenueSourceType[r.type],
    },
    { key: 'accounts', header: 'Accounts', align: 'right', render: (r) => r._count?.accounts ?? 0 },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ],
  schema: revenueSourceCreateSchema,
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'AppLovin' },
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      readOnlyOnEdit: true,
      placeholder: 'APPLOVIN',
    },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      required: true,
      options: enumOptions(REVENUE_SOURCE_TYPES, LABELS.revenueSourceType),
    },
    sortOrderField,
    statusField,
    { name: 'description', label: 'Description', type: 'textarea', wide: true },
  ],
  defaults: () => ({
    name: '',
    code: '',
    type: 'AD_NETWORK',
    sortOrder: '0',
    status: 'ACTIVE',
    description: '',
  }),
  toForm: (r) => ({
    name: r.name,
    code: r.code,
    type: r.type,
    sortOrder: String(r.sortOrder),
    status: r.status,
    description: str(r.description),
  }),
};

// ── Revenue accounts ─────────────────────────────────────────────────

const REVENUE_PAGES = {
  ALL: {
    resource: 'revenue-accounts',
    title: 'Revenue accounts',
    singular: 'Revenue account',
    placeholder: 'pub-xxxxxxxxxxxxxxxx',
  },
  ADMOB: {
    resource: 'admob-accounts',
    title: 'AdMob accounts',
    singular: 'AdMob account',
    placeholder: 'pub-xxxxxxxxxxxxxxxx',
  },
  ADX: {
    resource: 'adx-accounts',
    title: 'AdX accounts',
    singular: 'AdX account',
    placeholder: 'Ad Manager network code',
  },
} as const;

/**
 * Revenue accounts are independent masters; apps use them through assignment
 * periods. The AdMob / AdX pages are the same list limited to one source.
 */
function revenueAccountsPage(kind: keyof typeof REVENUE_PAGES): MasterConfig<RevenueAccount> {
  const page = REVENUE_PAGES[kind];
  const sourceCode = kind === 'ALL' ? null : kind;
  const sourceFilter: FilterDef = {
    name: 'revenueSourceId',
    label: 'Sources',
    remote: sources.revenueSource(sources.all),
  };
  return {
    resource: page.resource,
    title: page.title,
    singular: page.singular,
    description:
      'Revenue is entered in the account currency and converted to INR. Assign accounts to apps from the app page; one account can serve several apps.',
    searchPlaceholder: 'Search account, ID or email…',
    writePermission: 'masters.write',
    rowLink: (r) => `/revenue-accounts/${r.id}`,
    filters: sourceCode
      ? [appFilter, accountStatusFilter]
      : [appFilter, sourceFilter, accountStatusFilter],
    columns: [
      {
        key: 'accountName',
        header: 'Account',
        sortKey: 'accountName',
        render: (r) => (
          <div>
            <p className="font-medium text-slate-900">{r.accountName}</p>
            {(r.accountIdentifier || r.loginEmail) && (
              <p className="text-xs text-slate-500">
                {r.accountIdentifier && <span className="font-mono">{r.accountIdentifier}</span>}
                {r.accountIdentifier && r.loginEmail && ' · '}
                {r.loginEmail}
              </p>
            )}
          </div>
        ),
      },
      { key: 'apps', header: 'Used by', render: (r) => <UsedBy assignments={r.assignments} /> },
      {
        key: 'source',
        header: 'Source',
        sortKey: 'source',
        render: (r) => r.revenueSource?.name,
      },
      { key: 'currencyCode', header: 'Currency', sortKey: 'currencyCode' },
      {
        key: 'payoutThreshold',
        header: 'Threshold',
        align: 'right',
        render: (r) =>
          r.payoutThreshold ? (
            formatMoney(r.payoutThreshold, r.currencyCode)
          ) : (
            <span className="text-xs text-slate-400">Every month</span>
          ),
      },
      {
        key: 'bank',
        header: 'Payout bank',
        render: (r) => {
          const b = r.bankAccounts?.[0];
          return b ? (
            <span className="text-xs">
              {b.bankName}{' '}
              <span className="font-mono text-slate-500">{maskAccountNumber(b.accountNumber)}</span>
              {(b.accountHolderName || b.accountType) && (
                <span className="block text-slate-400">
                  {[b.accountHolderName, b.accountType && LABELS.bankAccountType[b.accountType]]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </span>
          ) : (
            <span className="text-xs text-slate-400">Not linked</span>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        sortKey: 'status',
        render: (r) => <StatusBadge status={r.status} />,
      },
    ],
    schema: revenueAccountCreateSchema,
    fields: [
      {
        name: 'revenueSourceId',
        label: 'Revenue source',
        type: 'select',
        required: true,
        remote: sources.revenueSource(),
      },
      {
        name: 'accountName',
        label: 'Account name',
        type: 'text',
        required: true,
        placeholder: 'Main AdMob account',
      },
      {
        name: 'accountIdentifier',
        label: 'Account ID',
        type: 'text',
        placeholder: page.placeholder,
      },
      { ...loginEmailField, help: 'Google login that owns this account' },
      accessEmailField,
      {
        name: 'currencyCode',
        label: 'Currency',
        type: 'select',
        required: true,
        remote: sources.currency(),
        help: 'Revenue is entered in this currency',
      },
      { name: 'gstAccountId', label: 'GST account', type: 'select', remote: sources.gstAccount() },
      {
        name: 'payoutThreshold',
        label: 'Payment threshold',
        type: 'decimal',
        help: 'Payout happens only when the unpaid balance reaches this (account currency). Smaller months carry to the next month. Leave empty on a new AdMob / AdX account for the standard $100 / €70 / £60.',
      },
      sortOrderField,
      accountStatusField,
      notesField,
    ],
    defaults: (l) => ({
      revenueSourceId: sourceCode
        ? (l?.revenueSources.find((x) => x.code === sourceCode)?.id ?? '')
        : '',
      accountName: '',
      accountIdentifier: '',
      loginEmail: '',
      accessEmail: '',
      currencyCode: 'USD',
      gstAccountId: '',
      payoutThreshold: '',
      sortOrder: '0',
      status: 'ACTIVE',
      notes: '',
    }),
    toForm: (r) => ({
      revenueSourceId: r.revenueSourceId,
      accountName: r.accountName,
      accountIdentifier: str(r.accountIdentifier),
      loginEmail: str(r.loginEmail),
      accessEmail: str(r.accessEmail),
      currencyCode: r.currencyCode,
      gstAccountId: str(r.gstAccountId),
      payoutThreshold: str(r.payoutThreshold),
      sortOrder: String(r.sortOrder),
      status: r.status,
      notes: str(r.notes),
    }),
  };
}

export const revenueAccountsConfig = revenueAccountsPage('ALL');
export const admobAccountsConfig = revenueAccountsPage('ADMOB');
export const adxAccountsConfig = revenueAccountsPage('ADX');

// ── Expense categories ───────────────────────────────────────────────

export const expenseCategoriesConfig: MasterConfig<ExpenseCategory> = {
  resource: 'expense-categories',
  title: 'Expense categories',
  singular: 'Category',
  description:
    'Categories for other (non-marketing) expenses such as salary, servers and software.',
  writePermission: 'masters.write',
  pageSize: 50,
  filters: [statusFilter],
  columns: [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
    },
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (r) => <code className="text-xs">{r.code}</code>,
    },
    { key: 'sortOrder', header: 'Order', align: 'right', sortKey: 'sortOrder' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ],
  schema: expenseCategoryCreateSchema,
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'code', label: 'Code', type: 'text', required: true, readOnlyOnEdit: true },
    sortOrderField,
    statusField,
  ],
  defaults: () => ({ name: '', code: '', sortOrder: '0', status: 'ACTIVE' }),
  toForm: (r) => ({ name: r.name, code: r.code, sortOrder: String(r.sortOrder), status: r.status }),
};

// ── Firebase accounts ────────────────────────────────────────────────

// ── Google Play developer accounts ───────────────────────────────────

const VERIFICATION_TONE = {
  NOT_STARTED: 'slate',
  PENDING: 'amber',
  VERIFIED: 'green',
  REJECTED: 'rose',
} as const;

export function VerificationBadge({
  status,
}: {
  status: PlayDeveloperAccount['verificationStatus'];
}) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  return <Badge tone={VERIFICATION_TONE[status]}>{LABELS.playVerificationStatus[status]}</Badge>;
}

export const playDeveloperAccountsConfig: MasterConfig<PlayDeveloperAccount> = {
  resource: 'play-developer-accounts',
  title: 'Google Play developer accounts',
  singular: 'Developer account',
  description:
    'Play Console accounts with their verification phone and documents and the device and internet used to access them. Apps link to their developer account automatically from the Play listing.',
  searchPlaceholder: 'Search developer, ID, email, device or app…',
  writePermission: 'masters.write',
  rowLink: (r) => `/play-developer-accounts/${r.id}`,
  filters: [
    {
      name: 'verificationStatus',
      label: 'Verification',
      options: enumOptions(PLAY_VERIFICATION_STATUSES, LABELS.playVerificationStatus),
    },
    accountStatusFilter,
  ],
  columns: [
    {
      key: 'name',
      header: 'Developer account',
      sortKey: 'name',
      render: (r) => (
        <div>
          <p className="font-medium text-slate-900">{r.name}</p>
          <p className="text-xs text-slate-500">
            {[r.developerId, r.accountType && LABELS.playAccountType[r.accountType]]
              .filter(Boolean)
              .join(' · ') || r.loginEmail}
          </p>
        </div>
      ),
    },
    {
      key: 'loginEmail',
      header: 'Login / access email',
      sortKey: 'loginEmail',
      render: (r) =>
        r.loginEmail || r.accessEmail ? (
          <span className="text-xs">
            {r.loginEmail}
            {r.accessEmail && <span className="block text-slate-500">{r.accessEmail}</span>}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'verificationStatus',
      header: 'Verification',
      sortKey: 'verificationStatus',
      render: (r) => (
        <div>
          <VerificationBadge status={r.verificationStatus} />
          {(r.verificationDevice || r.verificationPhone) && (
            <p className="mt-0.5 text-xs text-slate-500">
              {[r.verificationDevice, r.verificationPhone].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'accessDevice',
      header: 'Access device',
      render: (r) =>
        r.accessDevice || r.accessInternet ? (
          <span className="text-xs">
            {r.accessDevice}
            {r.accessInternet && <span className="block text-slate-500">{r.accessInternet}</span>}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'apps',
      header: 'Apps',
      render: (r) =>
        r.apps?.length ? (
          <span className="flex items-center gap-2">
            <AppIcons apps={r.apps} size={22} />
            <span className="text-xs text-slate-600">{r.apps.length}</span>
          </span>
        ) : (
          <span className="text-xs text-slate-400">No apps</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ],
  schema: playDeveloperAccountCreateSchema,
  fields: [
    {
      name: 'name',
      label: 'Developer name',
      type: 'text',
      required: true,
      placeholder: 'As shown on Google Play',
      help: 'Apps whose Play developer has this name link here automatically',
    },
    {
      name: 'developerId',
      label: 'Developer account ID',
      type: 'text',
      placeholder: 'Play Console ID',
    },
    {
      name: 'accountType',
      label: 'Account type',
      type: 'select',
      options: enumOptions(PLAY_ACCOUNT_TYPES, LABELS.playAccountType),
      emptyLabel: 'Not set',
    },
    { name: 'openedOn', label: 'Opened on', type: 'date' },
    { ...loginEmailField, help: 'Google login that owns the Play Console account' },
    accessEmailField,
    {
      name: 'openedOnDevice',
      label: 'Opened on device',
      type: 'text',
      placeholder: 'e.g. Redmi Note 12 / Office laptop',
      help: 'Mobile or computer the account was created on',
    },
    {
      name: 'verificationStatus',
      label: 'Verification status',
      type: 'select',
      options: enumOptions(PLAY_VERIFICATION_STATUSES, LABELS.playVerificationStatus),
      emptyLabel: 'Not set',
    },
    {
      name: 'verificationDevice',
      label: 'Verification mobile',
      type: 'text',
      placeholder: 'e.g. Samsung Galaxy A54',
      help: 'Mobile name / model used for Play Console verification',
    },
    {
      name: 'verificationPhone',
      label: 'Verification mobile number',
      type: 'text',
      placeholder: '+91 98xxxxxxx',
    },
    { name: 'verifiedOn', label: 'Verified on', type: 'date' },
    {
      name: 'accessDevice',
      label: 'Access device',
      type: 'text',
      placeholder: 'e.g. Dell laptop (Office)',
      help: 'Device used to log in to this account',
    },
    {
      name: 'accessInternet',
      label: 'Access device internet',
      type: 'text',
      placeholder: 'e.g. Jio Fiber, static IP 49.x.x.x',
      help: 'Internet used on the access device (SIM / Wi-Fi / ISP / fixed IP)',
    },
    {
      name: 'legalName',
      label: 'Legal name',
      type: 'text',
      help: 'About the developer (Play listing)',
    },
    { name: 'contactEmail', label: 'Developer email', type: 'text' },
    { name: 'phone', label: 'Developer phone', type: 'text' },
    { name: 'website', label: 'Website', type: 'text', placeholder: 'https://' },
    { name: 'address', label: 'Address', type: 'textarea', wide: true },
    sortOrderField,
    accountStatusField,
    notesField,
  ],
  defaults: () => ({
    name: '',
    developerId: '',
    accountType: '',
    openedOn: '',
    loginEmail: '',
    accessEmail: '',
    openedOnDevice: '',
    verificationStatus: '',
    verificationDevice: '',
    verificationPhone: '',
    verifiedOn: '',
    accessDevice: '',
    accessInternet: '',
    legalName: '',
    contactEmail: '',
    phone: '',
    website: '',
    address: '',
    sortOrder: '0',
    status: 'ACTIVE',
    notes: '',
  }),
  toForm: (r) => ({
    name: r.name,
    developerId: str(r.developerId),
    accountType: str(r.accountType),
    openedOn: str(r.openedOn),
    loginEmail: str(r.loginEmail),
    accessEmail: str(r.accessEmail),
    openedOnDevice: str(r.openedOnDevice),
    verificationStatus: str(r.verificationStatus),
    verificationDevice: str(r.verificationDevice),
    verificationPhone: str(r.verificationPhone),
    verifiedOn: str(r.verifiedOn),
    accessDevice: str(r.accessDevice),
    accessInternet: str(r.accessInternet),
    legalName: str(r.legalName),
    contactEmail: str(r.contactEmail),
    phone: str(r.phone),
    website: str(r.website),
    address: str(r.address),
    sortOrder: String(r.sortOrder),
    status: r.status,
    notes: str(r.notes),
  }),
};

export const firebaseAccountsConfig: MasterConfig<FirebaseAccount> = {
  resource: 'firebase-accounts',
  title: 'Firebase accounts',
  singular: 'Firebase account',
  description:
    'Google logins that host the Firebase projects of your apps. Link an app to its Firebase account on the app page.',
  searchPlaceholder: 'Search name, email or app…',
  writePermission: 'masters.write',
  filters: [statusFilter],
  columns: [
    {
      key: 'name',
      header: 'Account',
      sortKey: 'name',
      render: (r) => (
        <div>
          <p className="font-medium text-slate-900">{r.name}</p>
          {r.loginEmail && <p className="text-xs text-slate-500">{r.loginEmail}</p>}
        </div>
      ),
    },
    {
      key: 'accessEmail',
      header: 'Access email',
      render: (r) => r.accessEmail ?? <span className="text-slate-400">—</span>,
    },
    {
      key: 'apps',
      header: 'Apps',
      render: (r) =>
        r.apps?.length ? (
          <span className="flex items-center gap-2">
            <AppIcons apps={r.apps} size={22} />
            <span className="text-xs text-slate-600">{r.apps.map((a) => a.name).join(', ')}</span>
          </span>
        ) : (
          <span className="text-xs text-slate-400">No apps</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ],
  schema: firebaseAccountCreateSchema,
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Main Firebase' },
    { ...loginEmailField, help: 'Google login that owns this Firebase account' },
    accessEmailField,
    sortOrderField,
    statusField,
    notesField,
  ],
  defaults: () => ({
    name: '',
    loginEmail: '',
    accessEmail: '',
    sortOrder: '0',
    status: 'ACTIVE',
    notes: '',
  }),
  toForm: (r) => ({
    name: r.name,
    loginEmail: str(r.loginEmail),
    accessEmail: str(r.accessEmail),
    sortOrder: String(r.sortOrder),
    status: r.status,
    notes: str(r.notes),
  }),
};
